import React from "react";
import { Box, Divider, FormControl, FormLabel, Option, Switch } from "@mui/joy";
import type { ZekayConfig } from "../../../../api/config/Config.typed";
import type {
  SMSCompanySettings,
  SMSProviderDefinition,
} from "../../../../api/smsTemplates/SMSTemplate.typed";
import useAppSettings from "../../../../contexts/app/appSettings";
import { useAlert } from "../../../../contexts/app/notificationToast";
import { useSMSTemplate } from "../../../../contexts/api/smsTemplates";
import { CopyToClipboard } from "../../../../utils/copyToClipboard";
import DarkTypography from "../../../common/DarkTypography";
import DarkInput from "../../../common/DarkInput";
import DarkSelect from "../../../common/DarkSelect";
import IconButtonCTA from "../../../common/IconButtonCTA";
import { ContentCopyIcon } from "../../../../styles/StyledMUI";
import { textTokens } from "../../../../styles/globalStyles";
import SettingsSectionCard from "../../../shared/settingsSection/SettingsSectionCard";
import SettingsSectionEditToolbar from "../../../shared/settingsSection/SettingsSectionEditToolbar";

type CommDraft = {
  automaticMailing: boolean;
  provider: string;
  provider_token: string;
  sender: string;
};

type Props = {
  config: ZekayConfig;
  canEditBackend: boolean;
};

export default function CommunicationSettingsSection({ config, canEditBackend }: Props) {
  const { saveConfig } = useAppSettings();
  const { showAlert } = useAlert();
  const { getSMSSettings, updateSMSSettings, listSMSProviders } = useSMSTemplate();

  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [draft, setDraft] = React.useState<CommDraft | null>(null);
  const [smsSettings, setSmsSettings] = React.useState<SMSCompanySettings | null>(null);
  const [providers, setProviders] = React.useState<SMSProviderDefinition[]>([]);

  React.useEffect(() => {
    void (async () => {
      const [st, pvs] = await Promise.all([getSMSSettings(), listSMSProviders()]);
      if (st) setSmsSettings(st);
      if (pvs) setProviders(pvs);
    })();
  }, [getSMSSettings, listSMSProviders]);

  const buildDraft = React.useCallback((): CommDraft => {
    const sms = smsSettings;

    return {
      automaticMailing: config.AutomaticMailing,
      provider: sms?.provider ?? "",
      provider_token: "",
      sender: sms?.sender ?? "",
    };
  }, [config.AutomaticMailing, smsSettings]);

  const startEdit = () => {
    setDraft(buildDraft());
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  const save = React.useCallback(async () => {
    if (draft === null || saving) return;
    setSaving(true);
    try {
      await saveConfig({ ...config, AutomaticMailing: draft.automaticMailing });

      const payload: Partial<SMSCompanySettings> = {
        provider: draft.provider.trim(),
        sender: draft.sender.trim(),
      };
      if (draft.provider_token.trim()) {
        payload.provider_token = draft.provider_token.trim();
      }

      const next = await updateSMSSettings(payload);
      if (next) setSmsSettings(next);

      showAlert("Enregistré", "success");
      cancelEdit();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Échec de l'enregistrement";
      showAlert(msg, "danger");
    } finally {
      setSaving(false);
    }
  }, [config, draft, saveConfig, showAlert, saving, updateSMSSettings]);

  const view = editing && draft !== null ? draft : buildDraft();
  const fieldsEnabled = editing && canEditBackend;
  const unregisterLink = (config.UnregisterLink || "").trim();
  const tokenPlaceholder = smsSettings?.token_configured ? "Token enregistré" : "Token";
  const providerLabel =
    providers.find((p) => p.id === view.provider)?.label ||
    (view.provider ? view.provider : "Aucun");

  const handleCopyUnregister = React.useCallback(() => {
    void (async () => {
      const ok = await CopyToClipboard(config.UnregisterLink);
      showAlert(ok ? "Lien copié" : "Impossible de copier le lien", ok ? "success" : "danger");
    })();
  }, [config.UnregisterLink, showAlert]);

  return (
    <SettingsSectionCard
      title="Paramètres"
      titleEnd={
        <SettingsSectionEditToolbar
          canEditBackend={canEditBackend}
          isEditing={editing}
          saving={saving}
          onEdit={startEdit}
          onCancel={cancelEdit}
          onSave={() => void save()}
        />
      }
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <DarkTypography
          component="div"
          level="body-md"
          sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
        >
          Envoi automatique des mails
          <Switch
            checked={view.automaticMailing}
            onChange={(e) => {
              if (!fieldsEnabled || !draft) return;
              setDraft({ ...draft, automaticMailing: e.target.checked });
            }}
            disabled={!fieldsEnabled}
          />
        </DarkTypography>
      </Box>

      {unregisterLink ? (
        <>
          <Divider sx={{ my: 2 }} />
          <DarkTypography level="body-sm" colorVariant="muted" sx={{ mb: 1 }}>
            Newsletter
          </DarkTypography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "200px minmax(0, 1fr) auto" },
              gap: 2,
              alignItems: "center",
              width: "100%",
              maxWidth: { xs: "95vw", md: "none" },
            }}
          >
            <DarkTypography level="body-md" colorVariant="primary">
              Lien de désinscription
            </DarkTypography>
            <DarkInput value={unregisterLink} readOnly />
            <IconButtonCTA
              ctaVariant="accent"
              onClick={handleCopyUnregister}
              title="Copier"
              aria-label="Copier le lien de désinscription"
              sx={{ justifySelf: { xs: "start", md: "auto" } }}
            >
              <ContentCopyIcon />
            </IconButtonCTA>
          </Box>
        </>
      ) : null}

      <Divider sx={{ my: 2 }} />
      <DarkTypography level="body-sm" colorVariant="muted" sx={{ mb: 1.5 }}>
        SMS
      </DarkTypography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <FormControl>
          <FormLabel sx={{ color: textTokens.color.secondary }}>Provider</FormLabel>
          {fieldsEnabled ? (
            <DarkSelect
              value={view.provider}
              onChange={(_, v) => {
                if (!draft) return;
                setDraft({ ...draft, provider: String(v ?? "") });
              }}
            >
              <Option value="">Aucun</Option>
              {providers.map((p) => (
                <Option key={p.id} value={p.id}>
                  {p.label}
                </Option>
              ))}
            </DarkSelect>
          ) : (
            <DarkInput value={providerLabel} readOnly />
          )}
        </FormControl>
        <FormControl>
          <FormLabel sx={{ color: textTokens.color.secondary }}>Token</FormLabel>
          <DarkInput
            type="password"
            value={editing && draft ? draft.provider_token : ""}
            disabled={!fieldsEnabled}
            onChange={(e) => {
              if (!draft) return;
              setDraft({ ...draft, provider_token: e.target.value });
            }}
            placeholder={tokenPlaceholder}
          />
        </FormControl>
        <FormControl>
          <FormLabel sx={{ color: textTokens.color.secondary }}>Expéditeur</FormLabel>
          <DarkInput
            value={view.sender}
            disabled={!fieldsEnabled}
            onChange={(e) => {
              if (!draft) return;
              setDraft({ ...draft, sender: e.target.value });
            }}
          />
        </FormControl>
      </Box>
    </SettingsSectionCard>
  );
}
